import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import usePantallaEdit from "hooks/usePantallaEdit";
import { useRequestTracker } from "hooks/useResquestTracker";
import { CategoriaManager } from "managers";
import { GridView, SvgComponent } from "components";
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import ListHeader from "components/PantallaList/Header/ListHeader";
import { Simplebar } from "components/Simplebar/Simplebar";
import usePaginationValuesWhere from "hooks/usePaginationValuesWhere";
import Select from "react-select";
import { Categoria } from "models/ECF/Categoria";
import { DEFAULT_PAGINATION_VALUES } from "utils";
import SuspenseECF from "components/Suspense/SuspenseECF";
import Exportar from "components/Exportar/Exportar";
import CategoriaEdit from "./CategoriaEdit";


const CategoriaList = ({ showLoading, hideLoading, hideTitle, isActive }) => {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION_VALUES);
  const [list, setList] = useState([]);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const [showCanvas, setShowCanvas] = useState(false);
  const [categoria, setCategoriaState] = useState();
  const { Message, cerrarAlertas } = useMessage();
  const ref = useRef(null);
  const [showExportar, setShowExportar] = useState(false);
  const { trackRequest } = useRequestTracker();
  const ESTATUS_LIST = [
    { value: "", label: "Todos" },
    ...Categoria.ESTATUS_LIST,
  ];
  const {
    setEntidad: setCategoria,
    setEstadoCargado,
    setHayCambios,
  } = usePantallaEdit(setCategoriaState);

  useEffect(() => {
    if (isActive) {
      buscar();
    }
    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where, isActive]);

  useEffect(() => {
    if (categoria != undefined) {
      setShowCanvas(true);
    }
  }, [categoria]);

  const buscar = () => {
    const whereWithPaginationValues = { ...where, ...pagination };

    trackRequest({
      executeRequest: (signal) =>
        CategoriaManager.listado(whereWithPaginationValues, {
          signal,
        }),
      onSuccess: (res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);
        setList(res.Data);
      },
      onError: (error) => handleException(error),
      onFinally: () => hideLoading(),
    });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  const handleClick = (id) => {
    setEstadoCargado(false);
    if (id) {
      CategoriaManager.buscarPorId(id)
        .then((data) => {
          setCategoria(data);
        })
        .catch((e) => {
          handleException(e);
        })
        .finally(() => {
          hideLoading();
          setEstadoCargado(true);
        });
    } else {
      setCategoria({});
      setEstadoCargado(true);
    }
  };

  const grabar = () => {
    ref.current?.showLoading(true);
    if (!categoria.Img) {
      categoria.Img = [];
    }

    if (!categoria.IdSec) {
      CategoriaManager.crear(categoria)
        .then((res) => {
          Message({ message: `Grabado Exitosamente` });
          buscar();
          setCategoria(res);
          setShowCanvas(false);
        })
        .catch((e) => {
          handleException(e);
        })
        .finally(() => {
          ref.current?.showLoading(false);
          setHayCambios(false);
        });
    } else {
      CategoriaManager.actualizar(categoria)
        .then(() => {
          buscar();
          Message({ message: `Actualizado Exitosamente` });
          setShowCanvas(false);
        })
        .catch((e) => {
          handleException(e);
        })
        .finally(() => {
          ref.current?.showLoading(false);
          setHayCambios(false);
        });
    }
  };

  const categoriaEdit = (
    <Simplebar
      ref={ref}
      backdrop={"static"}
      confirmBack
      placement={"end"}
      portal={false}
      visible={showCanvas}
      setShow={(e) => {
        setShowCanvas(e);
      }}
      hideCallback={() => {
        setEstadoCargado(false);
      }}
      useLoading={true}
      title={
        categoria?.IdSec || false ? "Actualizar Categoría" : "Crear Categoría"
      }
      btnLeft={{
        show: true,
        message: "Grabar",
        isComponent: true,
        size: "45px",
        height: "24px",
        with: "24px",
        iconName: "save",
        isCenter: true,
        func: grabar,
      }}
      btnRight={{
        show: false,
        label: "Grabar",
        func: () => { },
      }}
    >
      <CategoriaEdit categoria={categoria} setCategoria={setCategoria} />
    </Simplebar>
  );

  const openExportar = () => {
    setShowExportar(true);
  };

  return (
    <div>
      {showExportar && (
        <Suspense fallback={<SuspenseECF />}>
          <Exportar
            PROPIEDADES_EXPORTAR={Categoria.PROPIEDADES_EXPORTAR}
            showExportar={showExportar}
            setShowExportar={setShowExportar}
            showLoading={showLoading}
            hideLoading={hideLoading}
            where={where}
            exportarCallBack={CategoriaManager.exportar}
            serialize={true}  
          ></Exportar>
        </Suspense>
      )}

      <div style={{ paddingRight: "6px" }}>
        <ListHeader
          titulo={hideTitle ? "" : "Categoría"}
          crearNuevo={() => handleClick()}
          top="0px"
          exportar={openExportar}
        ></ListHeader>
      </div>
      {showCanvas && (
        <Suspense fallback={<SuspenseECF />}>{categoriaEdit}</Suspense>
      )}
      <>
        <GridView
          pagination={pagination}
          setPagination={setPagination}
          allowPaging={pagination.EsPaginable}
          pageSize={pagination.CantidadPorPagina}
          pageIndex={pagination.IndiceDePagina}
          lastPageNumber={pagination.PaginasTotal}
          dataSource={list}
          onPageIndexChanging={onPageIndexChanging}
          where={where}
          setWhere={setWhere}
          height="70vh"
        >
          <GridView.Column
            isSticky
            bodyRenderer={(row) => {
              return (
                <>
                  <div className="float-right">
                    <IconButton onClick={() => handleClick(row.IdSec)}>
                      <ToolTip message="Editar">
                        <div>
                          <SvgComponent
                            iconName="pencil"
                            height="25"
                            width="25"
                          />
                        </div>
                      </ToolTip>
                    </IconButton>
                  </div>
                </>
              );
            }}
          />
          <GridView.Column
            headerText="Id"
            dataField="IdSec"
            sortExpression="IdSec"
            width="100px"
          />
          <GridView.Column
            headerText="Nombre"
            dataField="Nombre"
            sortExpression="Nombre"
          />
          <GridView.Column
            headerText="Estatus"
            dataField="Estatus"
            sortExpression="Estatus"
            filterRenderer={() => {
              return (
                <Select
                  className="react-select primary"
                  options={ESTATUS_LIST}
                  value={ESTATUS_LIST.filter(function (option) {
                    return (
                      option.value ===
                      (where.Estatus ? where.Estatus : ESTATUS_LIST[0].value)
                    );
                  })}
                  name="Estatus"
                  onChange={(e) =>
                    setWhere({
                      ...where,
                      Estatus: e.value,
                    })
                  }
                ></Select>
              );
            }}
          />
        </GridView>
      </>
    </div>
  );
};

export default CategoriaList;
